import {useContext, useRef} from 'react';
import * as React from 'react';
import {StoreContext} from "../../stores/StoreLoader";
import FontAwesome from "../utilities/FontAwesome";
import ClickableLink from "../utilities/ClickableLink";
import {IBreadcrumb} from "../../pages/MainGridPage";
import {setBlockHeaderTitle} from "../blocks/SchoolBlocks/blockUtils";
import {sb_delay} from "../../utils/functions";
import NotificationManager from "../notifications/NotificationManager";
import styles from "../../pages/styles/MainGridPage.module.scss";

let currentRequest;

function BreadcrumbIcon(props: {
    type: IBreadcrumb["type"],
}) {
    switch (props.type) {
        case "department":
        case "group":
            return <FontAwesome name={'fa-users'} prefix={'fas'} role="presentation" aria-hidden="true"/>;
        case "team":
            return <FontAwesome name={'fa-football-ball'} prefix={'fas'} role="presentation" aria-hidden="true"/>;
        case "section":
            return <FontAwesome name={'fa-sitemap'} prefix={'fas'} role="presentation" aria-hidden="true"/>;
        case "classroom":
            return <FontAwesome name={'fa-graduation-cap'} prefix={'fas'} role="presentation" aria-hidden="true"/>;
        case "page":
            return <FontAwesome name={'fa-file-alt'} prefix={'far'} role="presentation" aria-hidden="true"/>;
        default:
            return <FontAwesome name={'fa-sitemap'} prefix={'fas'} role="presentation" aria-hidden="true"/>;
    }
}

export function EditableTitle(props: {
    breadcrumb: IBreadcrumb,
    hasMicrosite?: boolean,
}) {
    const {label, type, model, url} = props.breadcrumb;
    const {i18nStore, organizationStore, userStore} = useContext(StoreContext);

    const editableTitleRef = useRef<HTMLSpanElement | null>(null);

    function saveTitle(e) {
        if (currentRequest) {
            currentRequest.abort();
        }

        currentRequest = setBlockHeaderTitle(i18nStore.currentOrgSupportedLocale, organizationStore.currentOrganization.id, {'title': editableTitleRef.current?.innerText},
            organizationStore.currentOrganization.id).done(function () {
            NotificationManager.success("Title updated successfully.");
        }).fail(function (xhr, tStatus, e) {
            if (tStatus === "abort") {
                // because we're aborting requests when another one comes, this isn't a real error
                return;
            }
            NotificationManager.error("Title update error.");
        });
    }

    function handleTitleKeydown(e) {
        if (13 == e.keyCode) {
            e.preventDefault();
            editableTitleRef.current?.blur();
            saveTitle(e);
            return false;
        } else {
            return true;
        }
    }

    function handleTitleInput(e) {
        sb_delay(function () {
            "use strict";
            saveTitle(e);
        }, 700);

        return false;
    }

    const isCurrentOrganization = organizationStore.currentOrganization.id === model?.id;

    if (organizationStore.currentOrganization.deleted && isCurrentOrganization) {
        return <span
            onKeyDown={handleTitleKeydown}
            onInput={handleTitleInput}
            className={styles.classroomTitle}
            data-id={organizationStore.currentOrganization.id}
            style={{textDecoration: "line-through"}}
        >
            {props.hasMicrosite || <BreadcrumbIcon type={type}/>}
            <span>{label}</span>
        </span>
    } else if (props.hasMicrosite && !isCurrentOrganization) {
        return <ClickableLink className={styles.classroomTitle} href={url}>
            {label}
        </ClickableLink>
    } else {
        return <span
            onKeyDown={handleTitleKeydown}
            onInput={handleTitleInput}
            className={styles.classroomTitle}
            data-id={organizationStore.currentOrganization.id}
            ref={editableTitleRef}
            contentEditable={userStore.editor}
        >
            {props.hasMicrosite || <BreadcrumbIcon type={type}/>}
            <span>{label}</span>
        </span>
    }
}

const Breadcrumbs: React.FC<{
    breadcrumbs: IBreadcrumb[],
    hasMicrosite?: boolean,
}> = props => {
    const {organizationStore, userStore, modalStore} = useContext(StoreContext);

    let {breadcrumbs} = props;
    if (props.hasMicrosite) {
        breadcrumbs = [organizationStore.currentOrganization.microsite.navigation[0]];
    }
    const lastBreadcrumbIndex = breadcrumbs.length - 1;

    return <div className={styles.breadcrumbs}>
        {breadcrumbs.map((breadcrumb, index) => {
            const itemKey = `${organizationStore.currentOrganization.id}-${breadcrumb?.model?.id || breadcrumb.url}`;

            if (index < lastBreadcrumbIndex) {
                return <span key={itemKey} className={styles.classroomTitle}>
                    <ClickableLink href={breadcrumb.url} className={`sb-organization-color-element-font`}>
                        <BreadcrumbIcon type={breadcrumb.type}/>&nbsp;
                        {breadcrumb.label}
                        {breadcrumb.isPrivate &&
                        <FontAwesome name={'fa-lock'} prefix={'fas'} title={`This is a private ${breadcrumb.type}`}
                                     style={{marginRight: "0.5em"}}
                                     aria-label={`This is a private ${breadcrumb.type}`}/>}

                    </ClickableLink>
                    {!(breadcrumbs[lastBreadcrumbIndex].type === 'page' && index + 1 === lastBreadcrumbIndex) &&
                    <FontAwesome name={'fa-chevron-right'} prefix={'fas'}/>}
                </span>
            } else if (breadcrumb.type !== 'page') {
                if (!props.hasMicrosite && organizationStore.currentOrganization.deleted) {
                    return <span key={itemKey} className={styles.classroomTitle}>
                        <EditableTitle breadcrumb={breadcrumb}/>
                        {userStore.editor && <>
                            <button id="btn-restore-organization"
                                    onClick={() => modalStore.addModal({
                                        type: "confirm",
                                        orgType: organizationStore.currentOrganization.type === "department" ? "group" : organizationStore.currentOrganization.type,
                                        orgID: organizationStore.currentOrganization.id,
                                        message: `Are you sure you want to restore this ${organizationStore.currentOrganization.type}?`
                                    })}
                                    className={`btn btn-default sb-organization-color-element-bg ${styles.restoreButton}`}>Restore
                            </button>
                        </>}
                    </span>
                } else {
                    return <EditableTitle key={itemKey} breadcrumb={breadcrumb} hasMicrosite={props.hasMicrosite}/>
                }
            }
        })}
    </div>
}

export default Breadcrumbs;
